import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, CardBody, Table, Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, FormGroup } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import axiosInstance from "../../components/Common/ApiManagement/axiosInstance";
import { ApiNames } from "../../components/Common/ApiManagement/apiServices";
import { toast } from "react-toastify";

const SecretMailEditor = () => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editModal, setEditModal] = useState(false);
    const [currentTemplate, setCurrentTemplate] = useState(null);
    const [previewModal, setPreviewModal] = useState(false);
    const [previewContent, setPreviewContent] = useState('');

    // Fetch all templates
    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(ApiNames.MAIL_TEMPLATES);
            if (res.data.status === 1) {
                setTemplates(res.data.data);
            }
        } catch (error) {
            console.error("Error fetching templates", error);
            toast.error("Failed to load templates");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    const toggleEdit = (template = null) => {
        if (template) {
            setCurrentTemplate(template);
        } else {
            setCurrentTemplate({
                title: '',
                slug: '',
                subject: '',
                from: '',
                content: ''
            });
        }
        setEditModal(!editModal);
    };

    const togglePreview = (content = '') => {
        // Fix images by replacing placeholders with actual URL
        // Assuming images might be relative or use {{BASE_URL}}
        let processedContent = content;
        if (content) {
            const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
            processedContent = content
                .replace(/{{BASE_URL}}/g, baseUrl)
                .replace(/{{APP_URL}}/g, window.location.origin)
                // If src is relative (starts with /), prepend API URL
                .replace(/src="\/([^"]+)"/g, `src="${baseUrl}/$1"`);
        }
        setPreviewContent(processedContent);
        setPreviewModal(!previewModal);
    };

    const handleSave = async () => {
        if (!currentTemplate) return;

        try {
            let res;
            if (currentTemplate._id) {
                res = await axiosInstance.put(ApiNames.MAIL_TEMPLATE_UPDATE(currentTemplate._id), currentTemplate);
            } else {
                res = await axiosInstance.post(ApiNames.MAIL_TEMPLATE_ADD, currentTemplate);
            }

            if (res.data.status === 1) {
                toast.success(currentTemplate._id ? "Template updated successfully" : "Template added successfully");
                fetchTemplates();
                setEditModal(false);
            }
        } catch (error) {
            console.error("Error saving template", error);
            toast.error("Failed to save template");
        }
    };

    const handleChange = (e) => {
        setCurrentTemplate({
            ...currentTemplate,
            [e.target.name]: e.target.value
        });
    };

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumbs title="Secret Admin" breadcrumbItem="Mail Template Editor" />

                <Row>
                    <Col xs={12}>
                        <Card>
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <h4 className="card-title mb-0">Email Templates</h4>
                                    <Button color="primary" onClick={() => toggleEdit()}>
                                        <i className="bx bx-plus me-1"></i> Add Template
                                    </Button>
                                </div>
                                {loading ? (
                                    <p>Loading...</p>
                                ) : (
                                    <div className="table-responsive" key={Date.now()}>
                                        <Table className="table table-bordered mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Title</th>
                                                    <th>Slug</th>
                                                    <th>Subject</th>
                                                    <th>From</th>
                                                    <th>Content (Preview)</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {templates.map((template) => (
                                                    <tr key={template._id}>
                                                        <td>{template.title}</td>
                                                        <td>{template.slug}</td>
                                                        <td>{template.subject}</td>
                                                        <td>{template.from}</td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            {/* Content hidden for safety, use Preview button */}
                                                            <span className="text-muted">Click Preview</span>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex gap-2">
                                                                <Button color="info" size="sm" onClick={() => togglePreview(template.content)}>
                                                                    Preview
                                                                </Button>
                                                                <Button color="primary" size="sm" onClick={() => toggleEdit(template)}>
                                                                    Edit
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Edit Modal */}
                <Modal isOpen={editModal} toggle={() => setEditModal(!editModal)} size="lg" centered={true}>
                    <ModalHeader toggle={() => setEditModal(!editModal)}>
                        {currentTemplate?._id ? `Edit Template: ${currentTemplate.title}` : 'Add New Template'}
                    </ModalHeader>
                    <ModalBody>
                        {currentTemplate && (
                            <>
                                <FormGroup>
                                    <Label>Title</Label>
                                    <Input
                                        type="text"
                                        name="title"
                                        value={currentTemplate.title || ''}
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label>Slug (Internal ID)</Label>
                                    <Input
                                        type="text"
                                        name="slug"
                                        value={currentTemplate.slug || ''}
                                        onChange={handleChange}
                                        disabled={!!currentTemplate._id}
                                        className={currentTemplate._id ? "bg-light" : ""}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label>From Address</Label>
                                    <Input
                                        type="text"
                                        name="from"
                                        value={currentTemplate.from || ''}
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label>Subject</Label>
                                    <Input
                                        type="text"
                                        name="subject"
                                        value={currentTemplate.subject || ''}
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label>Content (HTML)</Label>
                                    <Input
                                        type="textarea"
                                        name="content"
                                        rows={15}
                                        value={currentTemplate.content || ''}
                                        onChange={handleChange}
                                        style={{ fontFamily: 'monospace' }}
                                    />
                                </FormGroup>
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => toggleEdit()}>Cancel</Button>
                        <Button color="success" onClick={handleSave}>Save Changes</Button>
                    </ModalFooter>
                </Modal>

                {/* Preview Modal */}
                <Modal isOpen={previewModal} toggle={() => togglePreview()} size="lg" className="modal-xl" centered={true}>
                    <ModalHeader toggle={() => togglePreview()}>Template Preview</ModalHeader>
                    <ModalBody>
                        <div className="border p-3 rounded" style={{ minHeight: '400px', background: '#f8f9fa' }}>
                            <iframe
                                title="email-preview"
                                style={{ width: '100%', height: '500px', border: 'none', background: 'white' }}
                                srcDoc={previewContent}
                            />
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => togglePreview()}>Close</Button>
                    </ModalFooter>
                </Modal>

            </Container>
        </div>
    );
};

export default SecretMailEditor;
